import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";

import { api_v3_get_matches_by_tournament_with_date } from "../../utils/constants";

import { MatchSummary, Loading } from "../../components";

const CalendarHome = ({
  startDate,
  endDate,
  periods,
  milestones,
  tournamentId,
  divisions,
  isPrivate,
}) => {
  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [rawSelectedDay, setRawSelectedDay] = useState();
  const [showLegend, setShowLegend] = useState(true);

  const [loadingMatches, setLoadingMatches] = useState(false);
  const [errorMatches, setErrorMatches] = useState(false);
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);

  useEffect(() => {
    const loadMatches = async () => {
      setLoadingMatches(true);
      setErrorMatches(false);

      try {
        const response = await axios.get(
          api_v3_get_matches_by_tournament_with_date(tournamentId)
        );

        if (response.status === 200) {
          // Si la respuesta es exitosa, actualiza los partidos
          setMatches(response.data);
          setLoadingMatches(false);
          setErrorMatches(false);
        } else {
          console.log(`(${response.status}) response: ${response.data}`);
          setLoadingMatches(false);
          setErrorMatches(true);
        }
      } catch (error) {
        // Manejo específico para errores 404
        if (error.response && error.response.status === 404) {
          console.log("404: No se encontraron partidos.");
          setMatches([]); // Limpia los partidos
          setLoadingMatches(false); // Detén la carga
          setErrorMatches(false); // No se considera un error
        } else {
          // Maneja otros tipos de errores
          console.log("Error:", error);
          setLoadingMatches(false);
          setErrorMatches(true);
        }
      }
    };

    if (tournamentId) {
      loadMatches();
    }
  }, [tournamentId]);

  useEffect(() => {
    if (rawSelectedDay) {
      var filterMatches = matches.filter((match) => {
        const matchDate = new Date(match.matchDate);

        return (
          rawSelectedDay &&
          formatDateForComparison(matchDate) ===
          formatDateForComparison(rawSelectedDay)
        );
      });
      setFilteredMatches(filterMatches);
    }
  }, [rawSelectedDay, matches]);

  const togglePeriods = () => {
    setShowLegend(!showLegend);
  };

  const parseDate = (dateStr) => {
    return new Date(dateStr); // Usa el constructor Date para manejar fechas ISO
  };

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end) {
    // Maneja el caso donde start o end son null
    return <p>Error: Fechas inválidas</p>;
  }

  const getWeeksArray = () => {
    const oneDay = 24 * 60 * 60 * 1000;
    // Ajusta el día de inicio de la semana para que sea lunes en lugar de domingo
    let startDayOfWeek = start.getDay() - 1;
    // Si getDay() devuelve 0 (domingo), lo tratamos como el último día de la semana
    startDayOfWeek = startDayOfWeek === -1 ? 6 : startDayOfWeek;
    const adjustedStartDate = new Date(
      start.getTime() - startDayOfWeek * oneDay
    );
    const weeks = [];

    for (
      let date = new Date(adjustedStartDate);
      date < end;
      date = new Date(date.getTime() + 7 * oneDay)
    ) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(date.getTime() + i * oneDay);
        if (day >= start && day <= end) {
          week.push(day);
        } else {
          week.push(null);
        }
      }
      weeks.push(week);
    }

    return weeks;
  };

  const getMilestonesForDate = (date) => {
    if (!date) return [];

    return milestones.filter((milestone) => {
      const milestoneDate = parseDate(milestone.date);
      return milestoneDate.toDateString() === date.toDateString();
    });
  };

  const getMatchesForDate = (date) => {
    if (!date) return [];
    if (!matches) return [];

    return matches.filter((match) => {
      if (!match.matchDate) {
        return false;
      }

      const matchDate = new Date(match.matchDate);
      const selectedDate = new Date(date.toISOString());

      // Extraer el año, mes y día de cada fecha
      const matchDateYMD =
        matchDate.getFullYear() +
        "-" +
        (matchDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        matchDate.getDate().toString().padStart(2, "0");
      const selectedDateYMD =
        selectedDate.getFullYear() +
        "-" +
        (selectedDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        selectedDate.getDate().toString().padStart(2, "0");

      // Comparar las fechas ignorando la hora
      const areDatesEqual = matchDateYMD === selectedDateYMD;

      return areDatesEqual;
    });
  };

  const formatDate = (date) => {
    const days = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];
    const months = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];

    const weekDay = days[date.getDay()];
    const monthDay = date.getDate();
    const month = months[date.getMonth()];

    return `${weekDay} ${monthDay.toString().padStart(2, "0")}/${month}`;
  };

  const handleDayClick = (day) => {
    setRawSelectedDay(day);
    const dayMilestones = getMilestonesForDate(day);
    setSelectedDate(formatDate(day));
    setSelectedMilestones(dayMilestones);
  };

  const formatDateForComparison = (date) => {
    const d = new Date(date);
    const toRet = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
    //console.log(toRet);
    return toRet;
  };

  const getColorForDate = (date, hasMilestone, hasMatches) => {
    for (const period of periods) {
      const periodStart = parseDate(period.startDate);
      const periodEnd = parseDate(period.endDate);

      if (date >= periodStart && date <= periodEnd) {
        const color = period.color || "transparent";
        return hasMatches ? color : color + "33"; // "33" representa la transparencia del 20%
      }
    }
    return "transparent";
  };

  const weeks = getWeeksArray();

  const getMonthAbbreviation = (date) => {
    const monthAbbreviations = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    return monthAbbreviations[date.getMonth()];
  };

  const isCurrentDate = (date) => {
    if (date) {
      const currentDate = new Date();
      const selectedDate = new Date(date.toISOString());

      // Extraer el año, mes y día de cada fecha
      const currentDay =
        currentDate.getFullYear() +
        "-" +
        (currentDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        currentDate.getDate().toString().padStart(2, "0");
      const selectedDateYMD =
        selectedDate.getFullYear() +
        "-" +
        (selectedDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        selectedDate.getDate().toString().padStart(2, "0");

      // Comparar las fechas ignorando la hora
      const areDatesEqual = currentDay === selectedDateYMD;

      return areDatesEqual;
    }
    return false;
  };

  if (loadingMatches || !startDate || !endDate || !periods || !milestones) {
    return <Loading />;
  }

  return (
    <Wrapper className="glass-card" isPrivate={isPrivate}>
      <Column>
        <h4 className="gradient-text">Calendario</h4>
        {errorMatches ? (
          <p>
            <i>
              Ha ocurrido un error cargando la información de partidos. Se
              cargarán solo los eventos.
            </i>
          </p>
        ) : (
          ""
        )}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} style={{ display: "flex" }}>
            {week.map((day, dayIndex) => {
              const isSelected =
                day &&
                formatDateForComparison(day) ===
                formatDateForComparison(rawSelectedDay);
              return (
                <DayContainer
                  key={dayIndex}
                  onClick={() => day && handleDayClick(day)}
                >
                  {day && day.getDate() === 1 && (
                    <MonthBar>
                      <MonthLabel>{getMonthAbbreviation(day)}</MonthLabel>
                    </MonthBar>
                  )}
                    <DayBox
                      style={{
                        backgroundColor: day
                          ? getColorForDate(
                            day,
                            getMilestonesForDate(day).length > 0,
                            getMatchesForDate(day).length > 0
                          )
                          : "transparent",
                      }}
                      className={`day-box ${isCurrentDate(day) ? "current-day" : ""} ${isSelected ? "selected-day" : ""
                        }`}
                    >
                    {day ? day.getDate() : ""}
                    {getMilestonesForDate(day).length > 0 && (
                      <MilestoneIndicator />
                    )}
                  </DayBox>
                </DayContainer>
              );
            })}
          </div>
        ))}
      </Column>
      <Column>
        <Content>
          <h3 className="gradient-text">{selectedDate}</h3>{" "}
          {/* Asegúrate de formatear selectedDate para mostrarlo */}
          {selectedMilestones.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <h5>Eventos</h5>
            </div>
          )}
          {selectedMilestones.map((milestone, index) => (
            <MilestoneSummary key={index}>
              <MilestoneIndicatorSummary />
              {milestone.name}
            </MilestoneSummary>
          ))}
          {selectedDate && (
            <>
              <h5>Partidos</h5>
              <div className="matches-list">
                {filteredMatches.length > 0 ? (
                  filteredMatches.map((match) => (
                    <MatchSummary
                      key={match.matchId}
                      matchId={match.matchId}
                      status={match.status}
                      matchDate={match.matchDate}
                      playerAusername={match.playerAusername}
                      playerAresult={match.playerAresult}
                      playerAaw={match.playerAaw}
                      playerBusername={match.playerBusername}
                      playerBresult={match.playerBresult}
                      playerBaw={match.playerBaw}
                      divisionName={match.divisionName}
                    />
                  ))
                ) : (
                  <p>No hay partidos para este día.</p>
                )}
              </div>
            </>
          )}
          {!selectedDate && (
            <div style={{ marginTop: "1rem" }}>
              <h5>Notas</h5>
              <p>Selecciona un día para ver los partidos y eventos.</p>
              <p>El punto amarillo 🟡 indica que hay un evento ese día.</p>
              <p>
                Si el día está resaltado, quiere decir que hay eventos o
                partidos.
              </p>
            </div>
          )}
          <Legend>
            <div className="title" onClick={togglePeriods}>
              <i>Leyenda</i>
            </div>
            {showLegend &&
              periods.map(
                (period, periodIndex) =>
                  period.name && (
                    <Label
                      key={periodIndex}
                      style={{ backgroundColor: period.color }}
                    >
                      {period.name}
                    </Label>
                  )
              )}
          </Legend>
        </Content>
      </Column>
    </Wrapper>
  );
};

const Content = styled.div`
  margin-left: 1rem;
  padding: 1rem;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: start;
  overflow: hidden;
  margin-bottom: 2rem;

  .matches-list {
    margin-top: "1rem";
    max-height: 50vh;
    display: flex;
    flex-wrap: wrap;
    overflow: auto;
  }
`;

const MilestoneSummary = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin: 0.5rem;
`;

const MilestoneIndicatorSummary = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: yellow;
  margin-right: 0.5rem;
`;

const MilestoneIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: yellow;
  position: absolute;
  top: 20px;
  right: 20px;

  @media (max-width: 1000px) {
    width: 7px;
    height: 7px;
    top: 6px;
    right: 6px;
  }
`;

const Label = styled.div`
  padding: 0.2rem;
  border-radius: 0.75rem;
`;

const Legend = styled.div`
  position: absolute;
  bottom: 0;
  left: 1.5rem;
  display: flex;
  height: 3vh;
  .title {
    cursor: pointer;
  }

  * {
    margin-right: 0.8rem;
  }
`;

const Column = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  h4 {
    width: 100%;
    display: flex;
  }
  @media (max-width: 1270px) {
    width: 100%;
  }
`;

const Wrapper = styled.section`
  display: flex;
  width: 100%;

  border: 1px solid ${(props) => (props.isPrivate ? "yellow" : "transparent")};
  border-radius: 0.75rem;
  padding: 1rem;
  //margin: 1vh;

  @media (max-width: 1270px) {
    flex-direction: column;
  }
`;

const DayContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  .current-day {
    border: 2px solid var(--clr-green-dark);
  }
  .selected-day {
    background-color: var(--clr-dark-violet-2) !important;
  }
`;

const MonthBar = styled.div`
  position: absolute;
  left: 0px;
  width: 15px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MonthLabel = styled.span`
  transform: rotate(-90deg);
  color: var(--clr-white);
  font-size: 10px;
`;

const DayBox = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 5px;
  margin: 1rem;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 1000px) {
    margin: 0.2rem;
    padding: 0.5rem;
  }
`;

export default CalendarHome;
